// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {CopilotChat, type CopilotChatProps} from '@github-ui/copilot-chat/CopilotChat'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type GlobalAgentsMenuProps, GlobalAgentsMenuWithAnchor} from '@github-ui/global-agents-menu/GlobalAgentsMenu'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {GlobalCopilotMenu} from '@github-ui/global-copilot-menu/GlobalCopilotMenu'
import type {ReactPartialAnchorProps} from '@github-ui/react-core/react-partial-anchor'
import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {CopilotIcon} from '@primer/octicons-react'
import {ButtonGroup} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'

import {NavLinkItem} from '../../primitives/NavItemLink'
import baseStyles from '../../primitives/styles.module.css'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import styles from './CopilotItems.module.css'

export type CopilotItemsProps = {
  show?: boolean
  showCopilotTitle?: boolean
  showAgentsButton?: boolean
  copilotChatUrl?: string
  agentsPanel?: {
    popoverAvailable?: boolean
    repository: GlobalAgentsMenuProps['repository']
    agentsButtonId?: string
    showNotification?: boolean
  }
}

export function CopilotItems({
  show,
  showCopilotTitle,
  showAgentsButton,
  copilotChatUrl,
  agentsPanel,
}: CopilotItemsProps) {
  const {sendItemClick} = useGlobalNavAnalytics('COPILOT')

  // Fetch Copilot Chat props
  const {data: copilotChatProps, isPending} = useQuery({
    enabled: !!copilotChatUrl,
    queryKey: ['copilot-chat', copilotChatUrl],
    queryFn: async () => {
      if (!copilotChatUrl) {
        return null
      }

      const response = await verifiedFetchJSON(copilotChatUrl)

      if (response.ok) {
        try {
          return (await response.json()) as CopilotChatProps
        } catch {
          throw new Error('Failed to parse copilot chat response.')
        }
      }
      throw new Error('Failed to retrieve copilot chat payload.')
    },
  })

  // Initialize a reactPartialAnchor from the Copilot NavLink anchor element
  const anchorRef = useRef<HTMLAnchorElement>(null)
  const [reactPartialAnchor, setReactPartialAnchor] = useState<ReactPartialAnchorProps['reactPartialAnchor']>()
  useEffect(() => {
    if (anchorRef.current && !reactPartialAnchor) {
      setReactPartialAnchor({
        __wrapperElement: {
          anchor: anchorRef.current,
        },
      } as unknown as ReactPartialAnchorProps['reactPartialAnchor'])
    }
  }, [reactPartialAnchor])

  if (!show) {
    return null
  }

  const shouldUseAgentsHref =
    isFeatureEnabled('copilot_default_to_task_input') &&
    isFeatureEnabled('copilot_redirect_header_button_to_agents') &&
    showAgentsButton

  let analyticsKey = 'COPILOT_CHAT'
  if (isFeatureEnabled('copilot_default_to_task_input')) {
    analyticsKey += '_TASK_BUTTON'
  }
  if (isFeatureEnabled('copilot_default_to_task_input_control_group')) {
    analyticsKey += '_TASK_EXPERIMENT_CONTROL'
  }

  const copilotItemProps = {
    href: shouldUseAgentsHref ? '/copilot/agents' : '/copilot',
    icon: CopilotIcon,
    'aria-label': shouldUseAgentsHref ? 'Navigate to Copilot' : 'Chat with Copilot',
    analyticsKey,
    analyticsSubCategory: 'COPILOT',
    linkText: showCopilotTitle ? 'Copilot' : undefined,
    ref: anchorRef,
  }

  return (
    <span>
      <ButtonGroup>
        <NavLinkItem {...copilotItemProps} />
        {showAgentsButton && agentsPanel ? (
          <GlobalAgentsMenuWithAnchor
            {...agentsPanel}
            anchorProps={{
              variant: 'invisible',
              className: clsx(
                baseStyles.appHeaderButton,
                styles.AgentsMenu,
                agentsPanel.showNotification && styles.hasNotification,
              ),
              onClick: () => {
                sendItemClick('AGENTS_MENU')
              },
            }}
          />
        ) : (
          <GlobalCopilotMenu
            anchorProps={{
              variant: 'invisible',
              className: clsx(baseStyles.appHeaderButton, styles.CopilotMenu),
              onClick: () => {
                sendItemClick('COPILOT_MENU')
              },
            }}
          />
        )}
      </ButtonGroup>
      {!isPending && copilotChatProps && reactPartialAnchor && (
        <CopilotChat {...copilotChatProps} reactPartialAnchor={reactPartialAnchor} />
      )}
    </span>
  )
}

try{ CopilotItems.displayName ||= 'CopilotItems' } catch {}